import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Search, ChevronDown, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { groupByName } from "../lib/variants";
import { GOALS, goalForCategory } from "../lib/goals";
import { useCart } from "../context/CartContext";
import { Seo, siteOrigin } from "../components/Seo";
import { ProductCard } from "../components/ProductCard";
import { TestimonialStrip } from "../components/TestimonialStrip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "../components/ui/sheet";

export const SORTS = {
  featured: { label: "Featured", fn: (a, b) => (b.featured === a.featured ? a.name.localeCompare(b.name) : b.featured - a.featured) },
  name: { label: "Name A–Z", fn: (a, b) => a.name.localeCompare(b.name) },
  "price-asc": { label: "Price: Low to High", fn: (a, b) => a.price - b.price },
  "price-desc": { label: "Price: High to Low", fn: (a, b) => b.price - a.price },
};

function GoalSection({ goal, families, onAdd, defaultOpen, forceOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const expanded = forceOpen || open;
  return (
    <section id={`goal-${goal.slug}`} className="scroll-mt-44 mb-10 lg:mb-20" data-testid={`category-section-${goal.slug}`}>
      <div className="flex items-center gap-3 mb-5 sm:mb-8">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid={`category-section-toggle-${goal.slug}`}
          className="flex items-center gap-3 text-left lg:pointer-events-none min-w-0"
          aria-expanded={expanded}
        >
          <h2 className="font-display text-base sm:text-xl uppercase tracking-wide text-[#1A1A1A]">{goal.name}</h2>
          <span className="text-xs font-semibold text-[#8C7B6B] shrink-0">{families.length} {families.length === 1 ? "product" : "products"}</span>
          <ChevronDown className={`w-5 h-5 text-[#8C7B6B] shrink-0 lg:hidden transition-transform duration-300 ${expanded ? "rotate-180" : ""}`} />
        </button>
        <div className="flex-1 h-px bg-[#DDD7CF]" />
        {!goal.synthetic && (
          <Link to={`/shop/${goal.slug}`} data-testid={`category-section-link-${goal.slug}`} className="hidden sm:inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase font-semibold text-[#8C7B6B] hover:text-[#75665A] transition-colors shrink-0">
            Goal page <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
      <div className={`${expanded ? "grid" : "hidden"} lg:grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-10 sm:gap-x-8 sm:gap-y-14`}>
        {families.map((p, i) => <ProductCard key={p.id} p={p} i={i} onAdd={onAdd} />)}
      </div>
    </section>
  );
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [rawProducts, setRawProducts] = useState([]);
  const [goalSlug, setGoalSlug] = useState(params.get("goal") || "all");
  const [query, setQuery] = useState(params.get("q") || "");
  const [sortBy, setSortBy] = useState(SORTS[params.get("sort")] ? params.get("sort") : "featured");
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [barHidden, setBarHidden] = useState(false);
  const { addItem } = useCart();

  // The fixed Sort & Filter pill sits where the chat bubble lives on phones — lift the bubble while on this page
  useEffect(() => {
    document.body.classList.add("has-sticky-cta");
    return () => document.body.classList.remove("has-sticky-cta");
  }, []);

  useEffect(() => {
    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setBarHidden(y > last && y > 260);
      last = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/products")
      .then(({ data }) => setRawProducts(data))
      .catch(() => toast.error("Failed to load products"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const next = {};
    if (query.trim()) next.q = query.trim();
    if (goalSlug !== "all") next.goal = goalSlug;
    if (sortBy !== "featured") next.sort = sortBy;
    setParams(next, { replace: true });
  }, [query, goalSlug, sortBy, setParams]);

  const families = useMemo(() => groupByName(rawProducts), [rawProducts]);
  const activeGoal = GOALS.find((g) => g.slug === goalSlug);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = families;
    if (q) list = list.filter((p) => `${p.name} ${p.variants.map((v) => v.dosage).join(" ")} ${goalForCategory(p.category)?.name || p.category}`.toLowerCase().includes(q));
    if (activeGoal) list = list.filter((p) => p.category === activeGoal.category);
    return [...list].sort(SORTS[sortBy].fn);
  }, [families, query, activeGoal, sortBy]);

  const grouped = useMemo(() => {
    const known = GOALS.filter((g) => filtered.some((p) => p.category === g.category)).map((g) => [g, filtered.filter((p) => p.category === g.category)]);
    const extras = [...new Set(filtered.map((p) => p.category))].filter((c) => !goalForCategory(c));
    return [...known, ...extras.map((c) => [{ slug: c.toLowerCase().replace(/[^a-z0-9]+/g, "-"), name: c, category: c, synthetic: true }, filtered.filter((p) => p.category === c)])];
  }, [filtered]);

  const counts = useMemo(() => {
    const m = { all: families.length };
    GOALS.forEach((g) => { m[g.slug] = families.filter((p) => p.category === g.category).length; });
    return m;
  }, [families]);

  const handleAdd = (e, product) => {
    e.preventDefault();
    addItem(product);
    toast.success(`${product.name} ${product.dosage} added to cart`);
  };

  const searching = query.trim().length > 0;
  const filtersActive = goalSlug !== "all" || sortBy !== "featured";

  const goalPills = (testPrefix = "category-filter") => (
    [{ slug: "all", name: "All" }, ...GOALS.filter((g) => counts[g.slug] > 0)].map((g) => (
      <button
        key={g.slug}
        data-testid={`${testPrefix}-${g.slug}`}
        onClick={() => setGoalSlug(g.slug)}
        className={`shrink-0 text-xs tracking-[0.12em] uppercase font-semibold px-4 py-2 rounded-none border transition-colors duration-300 ${
          goalSlug === g.slug
            ? "bg-[#1A1A1A] text-[#F7F5F2] border-[#1A1A1A]"
            : "bg-transparent text-[#6E675F] border-[#DDD7CF] hover:border-[#8C7B6B] hover:text-[#1A1A1A]"
        }`}
      >
        {g.name}{counts[g.slug] ? ` · ${counts[g.slug]}` : ""}
      </button>
    ))
  );

  return (
    <div className="relative z-10">
      <Seo
        title="Shop All Research Peptides"
        path="/shop"
        description="Browse every research peptide and supply at Eternal Biolabs — filter by research goal, compare vial sizes and prices. Third-party tested, ships from Canada."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${siteOrigin()}/` },
            { "@type": "ListItem", position: 2, name: "Shop All", item: `${siteOrigin()}/shop` },
          ],
        }}
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-10 sm:pt-14 pb-6">
        <nav className="text-xs tracking-[0.15em] uppercase font-semibold text-[#6E675F] mb-4" data-testid="shop-breadcrumb">
          <Link to="/" className="hover:text-[#1A1A1A] transition-colors">Home</Link> <span className="mx-2 text-[#DDD7CF]">/</span> <span className="text-[#1A1A1A]">Shop All</span>
        </nav>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl uppercase tracking-wide text-[#1A1A1A]" data-testid="shop-heading">All research peptides</h1>
        <p className="text-sm sm:text-base text-[#6E675F] mt-3 max-w-2xl">
          {families.length ? `${families.length} products across ${GOALS.filter((g) => counts[g.slug] > 0).length} research goals.` : ""} Filter by goal, search a compound, or sort by price.
        </p>
      </section>

      <div className={`sticky top-16 z-30 bg-[#F7F5F2]/90 backdrop-blur-xl border-y border-[#DDD7CF]/60 transition-transform duration-300 ${barHidden ? "-translate-y-full lg:translate-y-0" : "translate-y-0"}`} data-testid="shop-sticky-bar">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3 flex flex-col gap-3">
          <div className="flex gap-3 items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C7B6B]" />
              <input
                data-testid="product-search-input"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search peptides… e.g. BPC-157"
                className="w-full bg-white border border-[#DDD7CF] rounded-none pl-11 pr-4 py-3 lg:py-2.5 text-base lg:text-sm text-[#1A1A1A] placeholder-[#6E675F]/60 focus:outline-none focus:border-[#8C7B6B] transition-colors"
              />
            </div>
            <div className="hidden lg:flex items-center gap-2">
              <span className="text-xs tracking-[0.15em] uppercase font-semibold text-[#6E675F]">Sort</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger data-testid="product-sort-select" className="w-[190px] rounded-none bg-white border-[#DDD7CF] text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SORTS).map(([k, s]) => (
                    <SelectItem key={k} value={k} data-testid={`sort-option-${k}`}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="hidden lg:flex gap-2 overflow-x-auto pb-1 -mb-1" data-testid="category-filter">
            {goalPills()}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-8 lg:pt-12 pb-8">
        {loading && (
          <div className="flex justify-center py-24">
            <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-24">
            <p className="text-[#1A1A1A] font-medium" data-testid="no-products-message">No products match "{query}"</p>
            <button onClick={() => { setQuery(""); setGoalSlug("all"); }} data-testid="clear-search-button" className="mt-4 text-xs tracking-[0.2em] uppercase font-semibold text-[#8C7B6B] hover:text-[#75665A]">
              Clear search
            </button>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <>
            {searching && (
              <p className="text-sm text-[#6E675F] mb-8" data-testid="search-results-count">
                {filtered.length} result{filtered.length === 1 ? "" : "s"} for "{query.trim()}"
              </p>
            )}
            {grouped.map(([goal, prods], idx) => (
              <GoalSection key={goal.slug} goal={goal} families={prods} onAdd={handleAdd} defaultOpen={idx === 0} forceOpen={searching || goalSlug !== "all"} />
            ))}
          </>
        )}
      </div>

      {!searching && goalSlug === "all" && <TestimonialStrip className="pb-24 lg:pb-16" />}

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetTrigger asChild>
          <button
            data-testid="mobile-filter-button"
            className="lg:hidden fixed bottom-5 left-1/2 -translate-x-1/2 z-40 inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F7F5F2] text-xs tracking-[0.15em] uppercase font-semibold px-6 py-3.5 rounded-none shadow-[0_10px_30px_-10px_rgba(26,26,26,0.5)]"
          >
            <SlidersHorizontal className="w-4 h-4" /> Sort & Filter
            {filtersActive && <span className="w-2 h-2 rounded-full bg-[#D4B36A]" data-testid="mobile-filter-active-dot" />}
          </button>
        </SheetTrigger>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[80vh] overflow-y-auto bg-[#F7F5F2]" data-testid="mobile-filter-sheet">
          <SheetHeader className="text-left">
            <SheetTitle className="font-display text-base uppercase tracking-wide text-[#1A1A1A]">Sort & Filter</SheetTitle>
          </SheetHeader>
          <div className="mt-5">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] mb-3">Sort by</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(SORTS).map(([k, s]) => (
                <button
                  key={k}
                  data-testid={`mobile-sort-${k}`}
                  onClick={() => setSortBy(k)}
                  className={`text-xs font-semibold px-4 py-3 rounded-none border transition-colors ${
                    sortBy === k ? "bg-[#1A1A1A] text-[#F7F5F2] border-[#1A1A1A]" : "bg-white text-[#6E675F] border-[#DDD7CF]"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-6">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] mb-3">Research goal</p>
            <div className="flex flex-wrap gap-2">
              {goalPills("mobile-category-filter")}
            </div>
          </div>
          <SheetClose asChild>
            <button
              data-testid="mobile-filter-apply"
              className="w-full mt-7 bg-[#8C7B6B] text-white text-xs tracking-[0.15em] uppercase font-semibold px-8 py-4 rounded-none"
            >
              Show {filtered.length} product{filtered.length === 1 ? "" : "s"}
            </button>
          </SheetClose>
        </SheetContent>
      </Sheet>
    </div>
  );
}
